import type { Rng } from '../../core/rng/Rng';
import { TILE, bfsDistances, type GeneratedMap, type TilePos } from '../../map/MapGen';
import type { RunState } from '../run/RunState';
import { generateShopStock } from '../run/shop';
import { rollArtifactDrop, rollArtifactProc } from '../artifacts/ArtifactSystem';
import type { ArtifactProc } from '../artifacts/ArtifactDefs';
import { rollQuality } from '../meta/economy';
import { tierEliteDropChance, tierQualityFloor } from '../run/tiers';
import { ELITE_AFFIXES, type EnemyType } from '../entities/enemyDefs';
import { CURSE_POOL, CURSES_BY_ID } from '../artifacts/CurseDefs';
import {
  BRAZIER_SLACK, BRAZIER_WINDOW_MAX, BRAZIER_WINDOW_MIN,
  SHRINE_BOON_POOL, SHRINE_BOONS_BY_ID,
  type AmbushElite, type FloorEvent, type FloorEventType, type ShrineEvent,
} from './EventDefs';
import { relightCandle } from '../characters/aspects';

/**
 * Rolls the floor's events at floor start from the per-floor `events` stream.
 *
 * FIXED DRAW ORDER (determinism contract, mirroring buildSchedule):
 *   1. floors ≥ 2 only: vault-door presence (1 draw; forceVaultDoor consumes
 *      the draw anyway so debug forcing can't shift the stream)
 *   2. event count (1 draw): floor 1 → 0–1, floors 2+ → 2–3 (ME3 density
 *      pass), clamped to the remaining draw-free candidates (candidate count
 *      is seed-determined)
 *   3. per event slot, in candidate order (pool composition depends only on
 *      seed-deterministic state: floor, used flags, remaining candidates):
 *      a. type (1 pick from the allowed pool — ≤1 each of
 *         shrine/strongbox/brazierRelay/spring per floor; strongbox needs
 *         ≥1 spare candidate, brazierRelay ≥2. The wayside MERCHANT was
 *         REMOVED from the pool 2026-07-28 — "too much stuff to buy",
 *         Sarah; the entrance shop is the one shop. Its type/branch stay
 *         as the re-enable switch.)
 *      b. contents:
 *         - merchant: (unreachable while unpooled) generateShopStock
 *           (2 draws ALWAYS: utility pick, buff pick — heal draw-free)
 *         - lootChest: content roll (1 draw, ALWAYS), then drop id +
 *           quality + proc ONLY when content='artifact' and id non-null
 *           (both the branch and pool exhaustion are seed-deterministic)
 *         - ambush: 3 × [drop gate, (id, quality, proc if drop), type, affix]
 *         - shrine: boon pick, curse pick
 *         - strongbox: consumes a SECOND candidate (draw-free) for the box;
 *           vault-chest grade drop: id, then quality + proc if id non-null
 *         - brazierRelay: consumes TWO more candidates (draw-free); reward
 *           chest rolled exactly like lootChest (content draw ALWAYS);
 *           windowTotal is pure BFS over the seeded map (draw-free)
 *         - spring: 0 draws
 *
 * Mutates `droppedArtifacts` (the run-wide without-replacement pool) — MUST
 * run at floor start, after buildSchedule, in fixed statement order relative
 * to it (see the World constructor).
 */
export function rollFloorEvents(
  rng: Rng,
  floor: number,
  map: GeneratedMap,
  droppedArtifacts: Set<string>,
  forceVaultDoor = false,
  tier = 0,
  healBlock: 'none' | 'all' | 'exceptLeech' = 'none',
): FloorEvent[] {
  const events: FloorEvent[] = [];
  const candidates = map.eventCandidates.slice();
  // Tier moves gate THRESHOLDS and roll FLOORS only — never draw counts.
  const dropChance = tierEliteDropChance(tier);
  const qualityFloor = tierQualityFloor(tier);

  // 1. Vault door — deepest candidate (vault doors live in dead ends).
  if (floor >= 2) {
    const doorRoll = rng.next() < 0.3;
    if ((doorRoll || forceVaultDoor) && candidates.length > 0) {
      events.push({ type: 'vaultDoor', pos: candidates.shift()! });
    }
  } else if (forceVaultDoor && candidates.length > 0) {
    events.push({ type: 'vaultDoor', pos: candidates.shift()! });
  }

  // 2. Event count (ME3: floors 2+ carry 2–3 — the chunk floors were empty).
  const wanted = floor === 1 ? rng.int(2) : 2 + rng.int(2);
  const count = Math.min(wanted, candidates.length);

  // 3. Events. Multi-candidate types (strongbox, brazierRelay) can drain
  // candidates faster than one per slot — the loop guard keeps the shifts
  // safe, and pool composition stays seed-deterministic either way.
  let shrineUsed = false;
  let strongboxUsed = false;
  let relayUsed = false;
  let springUsed = false;
  for (let i = 0; i < count && candidates.length > 0; i++) {
    const pos = candidates.shift()!;
    const pool: FloorEventType[] = ['lootChest', 'ambush'];
    if (!shrineUsed) pool.push('shrine');
    // Wayside merchant REMOVED from the pool (2026-07-28) — re-enable by
    // restoring the merchantUsed flag and:
    //   if (floor >= 2 && !merchantUsed) pool.push('merchant');
    if (!springUsed) pool.push('spring');
    if (!strongboxUsed && candidates.length >= 1) pool.push('strongbox');
    if (!relayUsed && candidates.length >= 2) pool.push('brazierRelay');
    const type = rng.pick(pool);

    switch (type) {
      case 'merchant': {
        // Unreachable while unpooled (see the pool comment above).
        events.push({ type: 'merchant', pos, stock: generateShopStock(rng, floor, healBlock) });
        break;
      }
      case 'lootChest': {
        const { content, drop } = rollChestContent(rng, droppedArtifacts, qualityFloor);
        events.push({ type: 'lootChest', pos, content, drop, opened: false });
        break;
      }
      case 'strongbox': {
        strongboxUsed = true;
        // Key at THIS slot's anchor, box at the next — farthest-point
        // ordering makes the two-stop route real. Second shift is draw-free.
        const boxPos = candidates.shift()!;
        // Vault-chest grade: id, then quality (floored 100%) + proc.
        const artifactId = rollArtifactDrop(rng, droppedArtifacts);
        const quality = artifactId !== null ? rollQuality(rng, Math.max(1.0, qualityFloor)) : 0;
        const procRoll = artifactId !== null
          ? rollArtifactProc(rng)
          : { proc: 'none' as const, curseId: null };
        events.push({ type: 'boneKey', pos, taken: false });
        events.push({
          type: 'strongbox', pos: boxPos, opened: false,
          drop: { artifactId, quality, proc: procRoll.proc, curseId: procRoll.curseId },
        });
        break;
      }
      case 'brazierRelay': {
        relayUsed = true;
        const b2 = candidates.shift()!;
        const b3 = candidates.shift()!;
        const { content, drop } = rollChestContent(rng, droppedArtifacts, qualityFloor);
        events.push({
          type: 'brazierRelay', pos, braziers: [pos, b2, b3],
          lit: [false, false, false], window: 0,
          windowTotal: relayWindow(map, pos, b2, b3),
          unsealed: false,
          chest: { type: 'lootChest', pos, content, drop, opened: false },
        });
        break;
      }
      case 'spring': {
        springUsed = true;
        events.push({ type: 'spring', pos, used: false });
        break;
      }
      case 'ambush': {
        const elites: AmbushElite[] = [];
        for (let e = 0; e < 3; e++) {
          const drops = rng.next() < dropChance ? rollArtifactDrop(rng, droppedArtifacts) : null;
          const quality = drops !== null ? rollQuality(rng, qualityFloor) : 0;
          const procRoll = drops !== null
            ? rollArtifactProc(rng)
            : { proc: 'none' as const, curseId: null };
          elites.push({
            // Mirror of SpawnDirector's elite pool (same exclusions).
            enemyType: rng.pick(['watcher', 'husk', 'chanter', 'brute', 'flanker'] as const) as EnemyType,
            affix: rng.pick(ELITE_AFFIXES),
            dropArtifactId: drops,
            dropQuality: quality,
            dropProc: procRoll.proc,
            dropCurseId: procRoll.curseId,
          });
        }
        events.push({ type: 'ambush', pos, elites, ordinalBase: 800000 + i * 8, triggered: false });
        break;
      }
      case 'shrine': {
        shrineUsed = true;
        events.push({
          type: 'shrine', pos, taken: false,
          offer: { boonId: rng.pick(SHRINE_BOON_POOL).id, curseId: rng.pick(CURSE_POOL).id },
        });
        break;
      }
      case 'vaultDoor':
      case 'boneKey':
        break; // never in the pool (boneKey rides the strongbox slot)
    }
  }
  return events;
}

/**
 * Brazier window from the ACTUAL walking route (draw-free — pure BFS over
 * the seeded map). The braziers land on farthest-point candidates, so a
 * flat window was unwinnable on big chunk floors. Budget = the two longest
 * pairwise legs (an upper bound on the remaining tour from ANY starting
 * brazier — the player may light them in any order), at player speed
 * 260 px/s with BRAZIER_SLACK fighting room, clamped [MIN, MAX].
 */
function relayWindow(map: GeneratedMap, b1: TilePos, b2: TilePos, b3: TilePos): number {
  const from1 = bfsDistances(map.tiles, map.w, map.h, b1.x, b1.y);
  const from2 = bfsDistances(map.tiles, map.w, map.h, b2.x, b2.y);
  const d12 = from1[b2.y * map.w + b2.x]!;
  const d13 = from1[b3.y * map.w + b3.x]!;
  const d23 = from2[b3.y * map.w + b3.x]!;
  const routePx = (d12 + d13 + d23 - Math.min(d12, d13, d23)) * TILE;
  const seconds = (routePx / 260) * BRAZIER_SLACK;
  return Math.min(BRAZIER_WINDOW_MAX, Math.max(BRAZIER_WINDOW_MIN, Math.round(seconds)));
}

/**
 * The lootChest content pattern, shared with the brazier relay's reward:
 * 1 content draw ALWAYS consumed, artifact rolls only on artifact content.
 */
function rollChestContent(
  rng: Rng, droppedArtifacts: Set<string>, qualityFloor: number,
): {
  content: 'artifact' | 'gems' | 'mend';
  drop: { artifactId: string | null; quality: number; proc: ArtifactProc; curseId: string | null };
} {
  const c = rng.next();
  const content = c < 0.6 ? 'artifact' as const : c < 0.85 ? 'gems' as const : 'mend' as const;
  let drop: { artifactId: string | null; quality: number; proc: ArtifactProc; curseId: string | null } =
    { artifactId: null, quality: 0, proc: 'none', curseId: null };
  if (content === 'artifact') {
    const artifactId = rollArtifactDrop(rng, droppedArtifacts);
    const quality = artifactId !== null ? rollQuality(rng, qualityFloor) : 0;
    const procRoll = artifactId !== null
      ? rollArtifactProc(rng)
      : { proc: 'none' as const, curseId: null };
    drop = { artifactId, quality, proc: procRoll.proc, curseId: procRoll.curseId };
  }
  return { content, drop };
}

/**
 * Apply the shrine trade to the run. Permanent for the run — deliberately NOT
 * registered in floorBuffs, so clearFloorBuffs never removes it. Each taken
 * curse gets its own `#n`-suffixed source so a duplicate curse id stays
 * independently removable (the vault cleansing altar cleanses one instance).
 */
export function acceptShrine(ev: ShrineEvent, runState: RunState): void {
  if (ev.taken) return;
  ev.taken = true;
  relightCandle(runState); // Wick: content interactions refuel
  const boon = SHRINE_BOONS_BY_ID.get(ev.offer.boonId);
  const curse = CURSES_BY_ID.get(ev.offer.curseId);
  const n = runState.shrineCurses.length;
  if (boon) runState.stats.addModifiers(`shrineBoon:${boon.id}#${n}`, boon.modifiers);
  if (curse) {
    const source = `shrineCurse:${curse.id}#${n}`;
    runState.stats.addModifiers(source, curse.modifiers);
    runState.shrineCurses.push({ curseId: curse.id, source });
  }
}
